import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "campaign-builder/ui/datepicker.css";
import { CalendarPlus } from "lucide-react";
import cn from "classnames";

export interface LaunchDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  error?: boolean;
  placeholder?: string;
  /** react-datepicker display format, country-keyed via getDatePickerDateFormat. */
  dateFormat?: string;
  /** Bumped to imperatively open the calendar — the F9 Edit-launch-date link. */
  openSignal?: number;
}

interface TriggerProps {
  value?: string;
  onClick?: () => void;
  error?: boolean;
  placeholder?: string;
}

const Trigger = React.forwardRef<HTMLButtonElement, TriggerProps>(({ value, onClick, error, placeholder }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    aria-label={value ? `Launch date: ${value}` : placeholder ?? "Select a launch date"}
    className={cn(
      "flex h-9 w-full items-center justify-between rounded-md border border-solid bg-background pl-3 pr-2 text-left text-sm focus:outline-none focus-visible:border-slate-400 dark:focus-visible:border-slate-500 focus-visible:ring-1 focus-visible:ring-slate-200 dark:focus-visible:ring-slate-700",
      error ? "border-destructive" : "border-border"
    )}
  >
    <span className={value ? "text-foreground" : "text-muted-foreground"}>{value || placeholder}</span>
    <CalendarPlus className="text-foreground h-[18px] w-[18px]" strokeWidth={1.5} />
  </button>
));
Trigger.displayName = "LaunchDatePickerTrigger";

export const LaunchDatePicker = ({
  value,
  onChange,
  minDate,
  error,
  placeholder = "Select a date to launch",
  dateFormat = "MM/dd/yyyy",
  openSignal,
}: LaunchDatePickerProps) => {
  const ref = useRef<ReactDatePicker>(null);
  // Opens the calendar when the Edit-launch-date link fires. openSignal is a
  // monotonic nonce starting at 1, so a falsy value (undefined on mount, or 0)
  // never opens it — the picker only opens on a real Edit click.
  useEffect(() => {
    if (!openSignal) return;
    ref.current?.setOpen(true);
  }, [openSignal]);

  return (
    <ReactDatePicker
      ref={ref}
      selected={value}
      onChange={onChange}
      minDate={minDate}
      dateFormat={dateFormat}
      popperPlacement="bottom-start"
      popperClassName="cb-datepicker-popper"
      calendarClassName="cb-datepicker"
      wrapperClassName="block w-full"
      popperContainer={({ children }) => createPortal(children, document.body)}
      customInput={<Trigger error={error} placeholder={placeholder} />}
    />
  );
};
